import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { userSelector } from '../../../../../../redux/reducers/userReducer';
import FileUploader from '../../../../../../extras/fileUploader';
import LoadingCircleIcon from '../../../../../../icons/LoadingCircle';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';
import { uploadIsa, fetchISAUrl } from '../../../../../../services/apiService';

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    ['link'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};

const stripHtml = (html) => html?.replace(/<[^>]+>/g, '').trim() || '';

const UploadIsa = ({ userParams, uploadIsaData, setRefreshTrigger }) => {
  const [file, setFile] = useState(null);
  const [agreementExpiry, setAgreementExpiry] = useState('');
  const [threshold, setThreshold] = useState('');
  const [remarks, setRemarks] = useState([]);
  const [newRemark, setNewRemark] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [existingIsaUrl, setExistingIsaUrl] = useState(null);
  console.log(uploadIsaData, 'uploaduissa');

  const user = useSelector(userSelector);

  useEffect(() => {
    if (uploadIsaData && Array.isArray(uploadIsaData) && uploadIsaData.length > 0) {
      const data = uploadIsaData[0];
      if (data.threshold !== undefined && data.threshold !== null) setThreshold(data.threshold.toString());
      if (data.agreementExpiry) setAgreementExpiry(data.agreementExpiry.split('T')[0]);
      if (data.remarks && Array.isArray(data.remarks)) setRemarks(data.remarks);
      
      if (data.isaUrl) {
        const fetchSignedUrl = async () => {
          try {
            // Extract the S3 key from the full URL (e.g., 'isa/b079...')
            const urlObj = new URL(data.isaUrl);
            const itemKey = urlObj.pathname.substring(1);
            
            const response = await fetchISAUrl(itemKey);
            if (response && response.signedUrl) {
              setExistingIsaUrl(response.signedUrl);
            } else {
              setExistingIsaUrl(data.isaUrl); // fallback
            }
          } catch (error) {
            console.error('Error fetching signed URL for ISA PDF:', error);
            setExistingIsaUrl(data.isaUrl); // fallback
          }
        };
        fetchSignedUrl();
      }
    }
  }, [uploadIsaData]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    
    // Clean up the object URL to avoid memory leaks
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleThresholdChange = (e) => {
    const value = e.target.value;
    // Allow only unsigned integers (digits only)
    if (value === '' || /^\d+$/.test(value)) {
      setThreshold(value);
    }
  };
  const handleOpenUrl = async (url) => {
    try {
      const urlObj = new URL(url);
      const itemKey = urlObj.pathname.substring(1);

      const response = await fetchISAUrl(itemKey);
      const finalUrl = response?.signedUrl || url;

      window.open(finalUrl, '_blank');
    } catch (err) {
      console.error('Error opening ISA URL:', err);
      window.open(url, '_blank'); // fallback
    }
  };
  const addRemark = () => {
    const plainText = stripHtml(newRemark);
    if (!plainText || !user) return;
    const timestamp = new Date().toLocaleString();
    const remarkEntry = {
      id: Date.now(),
      evaluator:
        (user?.firstName || '') + ' ' + (user?.lastName || '') || user?.email || 'Unknown User',
      userId: user?.id || '',
      remark: newRemark,
      timestamp: timestamp,
    };
    setRemarks((prev) => [...prev, remarkEntry]);
    setNewRemark('');
  };

  const handleConfirm = async () => {
    if (!file && !existingIsaUrl) return;
    setLoading(true);
    try {
      const data = new FormData();
      if (file) data.append('isaPdf', file);
      data.append('agreementExpiry', agreementExpiry);
      data.append('threshold', threshold);
      data.append('remarks', JSON.stringify(remarks));
      data.append('studentId', userParams?.userId);
      data.append('courseName', userParams?.courseType);
    
      const response = await uploadIsa(data);

      if(response){
        toast.success(response?.message || 'ISA PDF successfully uploaded!');
        setFile(null);
        setAgreementExpiry('');
        setThreshold('');
        setRemarks([]);
        setNewRemark('');
        
        // Trigger a re-fetch of the metrics API automatically
        if (setRefreshTrigger) {
          setRefreshTrigger((prev) => prev + 1);
        }
      }

    } catch (error) {
      console.error('Upload ISA error:', error);
      toast.error('Failed to upload ISA PDF. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='bg-white rounded-md p-6'>
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
        {/* Left Column - PDF Upload & Preview */}
        <div className='flex flex-col gap-6'>
          {(previewUrl || existingIsaUrl) && (
            <div className='flex flex-col flex-1 border border-gray-200 rounded-md overflow-hidden min-h-[500px]'>
              <iframe
                src={`${
                  previewUrl || existingIsaUrl
                }#toolbar=0&navpanes=0&scrollbar=0`}
                className='w-full h-full'
                title='PDF Preview'
              />
            </div>
          )}
        </div>

        {/* Right Column - Form Details */}
        <div className='flex flex-col gap-6'>
          <h2 className='text-xl font-semibold mb-4'>Details</h2>

          {/* Agreement Expiry Section */}
          <div className='flex flex-col sm:flex-row gap-6'>
            {/* Agreement Expiry */}
            <div className='flex flex-col gap-2 w-full sm:w-64'>
              <label
                htmlFor='agreement-expiry'
                className='block text-sm font-semibold text-gray-700'
              >
                Agreement Expiry
              </label>
              <input
                id='agreement-expiry'
                type='date'
                value={agreementExpiry}
                onChange={(e) => setAgreementExpiry(e.target.value)}
                className='border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200 w-full'
              />
            </div>

            {/* Threshold */}
            <div className='flex flex-col gap-2 w-full sm:w-64'>
              <label
                htmlFor='threshold'
                className='block text-sm font-semibold text-gray-700'
              >
                Threshold
              </label>
              <input
                id='threshold'
                type='text'
                inputMode='numeric'
                value={threshold}
                onChange={handleThresholdChange}
                className='border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200 w-full'
                placeholder='Enter threshold value'
              />
            </div>

            <div>
              <h2 className='text-xl font-semibold mb-4'>Upload ISA PDF</h2>
              <FileUploader file={file} setFile={setFile} />
            </div>
          </div>

          {/* Remarks / Notes Section */}
          <div className='flex flex-col gap-2'>
            <label className='block text-sm font-semibold text-gray-700'>
              Remarks / Notes
            </label>
            {/* Existing Remarks List */}
            {remarks.length > 0 && (
              <div
                className={`border border-gray-200 rounded p-0 bg-gray-50 ${
                  remarks.length > 2 ? 'max-h-48 overflow-y-auto' : ''
                }`}
              >
                {[...remarks].reverse().map((remark) => (
                  <div
                    key={remark.id}
                    className='mb-2 p-3 bg-white rounded border-l-4 border-blue-500'
                  >
                    <div className='flex justify-between items-start mb-1'>
                      <span className='text-xs font-semibold text-blue-600'>
                        {remark.evaluator}
                      </span>
                      <span className='text-xs text-gray-500'>
                        {remark.timestamp}
                      </span>
                    </div>

                    <div
                      className='text-sm text-gray-700'
                      dangerouslySetInnerHTML={{ __html: remark.remark }}
                    />

                    {/* ✅ Show button if URL exists */}
                    {remark.url && (
                      <button
                        onClick={() => handleOpenUrl(remark.url)}
                        className='mt-2 text-blue-600 text-xs underline hover:text-blue-800'
                      >
                        View Old ISA PDF
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {/* Add new remark */}
            <div className='flex flex-col gap-2 mt-2'>
              <div className='bg-white rounded-md border border-gray-300'>
                <ReactQuill
                  theme='snow'
                  value={newRemark}
                  onChange={(value) => setNewRemark(value)}
                  placeholder='Add new remark...'
                  modules={quillModules}
                  className='h-[150px]'
                />
              </div>

              <div className='flex justify-end mt-1'>
                <button
                  type='button'
                  onClick={addRemark}
                  disabled={!stripHtml(newRemark)}
                  className='px-5 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-semibold'
                >
                  Add
                </button>
              </div>
            </div>{' '}
          </div>

          {/* Confirm Button */}
          <div className='mt-auto pt-6'>
            <button
              onClick={handleConfirm}
              disabled={loading}
              className={`text-white px-8 py-3 rounded-md font-medium w-full sm:w-auto min-w-[144px] bg-red-c3 ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? (
                <span className='flex items-center justify-center'>
                  <LoadingCircleIcon iconStyle='w-5 h-5 mr-2 animate-spin' />
                  Saving...
                </span>
              ) : (
                'Confirm & Submit'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadIsa;